// frontend/src/pages/ordenes/components/inline-cliente-form.tsx
//
// Formulario inline para crear un cliente directamente desde OrdenFormDialog.
// Se renderiza DENTRO del Dialog (no en un portal separado) para evitar
// conflictos con el bloqueo de interacción de Radix UI Dialog.

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Loader2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCreateCliente } from "@/hooks/use-clientes";
import type { ClienteListItem } from "@/hooks/use-clientes";

// ─── Schema ──────────────────────────────────────────────────────────────────

const rtnRegex = /^\d{13,14}$/;

const clienteSchema = z.object({
  nombre: z.string().min(2, "ordenes.inline.validation.nameMin"),
  rtn: z
    .string()
    .optional()
    .refine((v) => !v || rtnRegex.test(v), {
      message: "ordenes.inline.validation.rtnFormat",
    }),
  direccion: z.string().optional(),
  contacto_nombre: z.string().optional(),
  contacto_telefono: z.string().optional(),
});

type ClienteFormValues = z.infer<typeof clienteSchema>;

// ─── Props ────────────────────────────────────────────────────────────────────

interface InlineClienteFormProps {
  initialName?: string;
  sucursalId: number;
  onSuccess: (cliente: ClienteListItem) => void;
  onCancel: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

export function InlineClienteForm({
  initialName = "",
  sucursalId,
  onSuccess,
  onCancel,
}: InlineClienteFormProps) {
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ClienteFormValues>({
    resolver: zodResolver(clienteSchema),
    defaultValues: { nombre: initialName },
  });

  // Pre-rellenar nombre solo al montar (initialName es el searchQuery al momento de abrir)
  useEffect(() => {
    reset({
      nombre: initialName,
      rtn: "",
      direccion: "",
      contacto_nombre: "",
      contacto_telefono: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createCliente = useCreateCliente();

  const onSubmit = (values: ClienteFormValues) => {
    createCliente.mutate(
      {
        nombre: values.nombre,
        sucursal: sucursalId,
        rtn: values.rtn || undefined,
        direccion: values.direccion || undefined,
        contacto_nombre: values.contacto_nombre || undefined,
        contacto_telefono: values.contacto_telefono || undefined,
      },
      {
        onSuccess: (clienteCreado) => {
          onSuccess(clienteCreado as ClienteListItem);
          reset();
        },
      }
    );
  };

  return (
    <div className="rounded-md border bg-muted/30 p-3 space-y-3" data-testid="inline-cliente-form">
      <p className="text-sm font-medium text-foreground">{t("ordenes.inline.newClient")}</p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
        {/* Nombre */}
        <div className="space-y-1">
          <Label htmlFor="inline-cliente-nombre" className="text-xs">
            {t("ordenes.inline.fields.clientName")}
          </Label>
          <Input
            id="inline-cliente-nombre"
            {...register("nombre")}
            disabled={createCliente.isPending}
            data-testid="inline-cliente-nombre"
            placeholder={t("ordenes.inline.fields.clientName")}
            className="h-8 text-sm"
          />
          {errors.nombre && (
            <p className="text-xs text-destructive">{t(errors.nombre.message ?? "")}</p>
          )}
        </div>

        {/* RTN */}
        <div className="space-y-1">
          <Label htmlFor="inline-cliente-rtn" className="text-xs">
            {t("ordenes.inline.fields.rtn")}
          </Label>
          <Input
            id="inline-cliente-rtn"
            {...register("rtn")}
            disabled={createCliente.isPending}
            data-testid="inline-cliente-rtn"
            placeholder="0801199012345"
            maxLength={14}
            className="h-8 text-sm"
          />
          {errors.rtn && <p className="text-xs text-destructive">{t(errors.rtn.message ?? "")}</p>}
        </div>

        {/* Dirección */}
        <div className="space-y-1">
          <Label htmlFor="inline-cliente-direccion" className="text-xs">
            {t("ordenes.inline.fields.address")}
          </Label>
          <Input
            id="inline-cliente-direccion"
            {...register("direccion")}
            disabled={createCliente.isPending}
            data-testid="inline-cliente-direccion"
            placeholder={t("ordenes.inline.fields.address")}
            className="h-8 text-sm"
          />
        </div>

        {/* Contacto nombre */}
        <div className="space-y-1">
          <Label htmlFor="inline-cliente-contacto-nombre" className="text-xs">
            {t("ordenes.inline.fields.contactName")}
          </Label>
          <Input
            id="inline-cliente-contacto-nombre"
            {...register("contacto_nombre")}
            disabled={createCliente.isPending}
            data-testid="inline-cliente-contacto-nombre"
            placeholder={t("ordenes.inline.fields.contactName")}
            className="h-8 text-sm"
          />
        </div>

        {/* Contacto teléfono */}
        <div className="space-y-1">
          <Label htmlFor="inline-cliente-contacto-telefono" className="text-xs">
            {t("ordenes.inline.fields.contactPhone")}
          </Label>
          <Input
            id="inline-cliente-contacto-telefono"
            {...register("contacto_telefono")}
            disabled={createCliente.isPending}
            data-testid="inline-cliente-contacto-telefono"
            placeholder={t("ordenes.inline.fields.contactPhone")}
            className="h-8 text-sm"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            type="submit"
            size="sm"
            disabled={createCliente.isPending}
            className="flex-1"
            data-testid="inline-cliente-submit"
          >
            {createCliente.isPending && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
            {t("common.save")}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={createCliente.isPending}
            onClick={onCancel}
            data-testid="inline-cliente-cancel"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </form>
    </div>
  );
}
